import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  ArrowLeft, Eye, DollarSign, TrendingUp, ShoppingCart, 
  Percent, Plus, Trash2, Loader2, BarChart3, Tag, Wallet
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const InstructorAnalytics = () => {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [coupons, setCoupons] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [couponDialog, setCouponDialog] = useState(false);
  const [payoutDialog, setPayoutDialog] = useState(false);
  const [newCoupon, setNewCoupon] = useState({ code: '', discount_percent: 10, max_uses: 100 });
  const [payoutAmount, setPayoutAmount] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [analyticsRes, couponsRes, payoutsRes] = await Promise.all([
        axios.get(`${API}/instructor/analytics`, { headers }),
        axios.get(`${API}/coupons`, { headers }),
        axios.get(`${API}/instructor/payouts`, { headers })
      ]);
      setAnalytics(analyticsRes.data);
      setCoupons(couponsRes.data);
      setPayouts(payoutsRes.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const createCoupon = async () => {
    if (!newCoupon.code || newCoupon.discount_percent <= 0) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      await axios.post(`${API}/coupons`, newCoupon, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Coupon created!');
      setCouponDialog(false);
      setNewCoupon({ code: '', discount_percent: 10, max_uses: 100 });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create coupon');
    }
  };

  const deleteCoupon = async (couponId) => {
    if (!window.confirm('Delete this coupon?')) return;
    
    try {
      await axios.delete(`${API}/coupons/${couponId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Coupon deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete coupon');
    }
  };

  const requestPayout = async () => {
    const amount = parseFloat(payoutAmount);
    if (isNaN(amount) || amount < 10) {
      toast.error('Minimum payout is $10');
      return;
    }

    try {
      await axios.post(`${API}/instructor/request-payout`, { amount }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Payout requested!');
      setPayoutDialog(false);
      setPayoutAmount('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request payout');
    }
  };

  // Transform data for charts
  const getViewsChartData = () => {
    if (!analytics?.views_by_date) return [];
    return Object.entries(analytics.views_by_date)
      .sort((a, b) => a[0].localeCompare(b[0]))
      .slice(-14)
      .map(([date, views]) => ({ date: date.slice(5), views }));
  };

  const getSalesChartData = () => {
    if (!analytics?.sales_by_date) return [];
    return Object.entries(analytics.sales_by_date)
      .sort((a, b) => a[0].localeCompare(b[0]))
      .slice(-14)
      .map(([date, data]) => ({ date: date.slice(5), sales: data.count, revenue: data.revenue }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="pt-24 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  const pendingPayout = user?.pending_payout || 0;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="instructor-analytics">
      <Navbar />

      <div className="pt-24 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate('/instructor')} className="-ml-4">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div>
                <h1 className="font-heading text-3xl font-bold text-slate-900">Analytics & Earnings</h1>
                <p className="text-slate-600">Track your performance and manage payouts</p>
              </div>
            </div>
          </div>

          {/* Overview Stats */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <Card className="border-slate-200">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-blue-100 text-blue-600 flex items-center justify-center">
                    <Eye className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{analytics?.overview?.total_views || 0}</p>
                    <p className="text-sm text-slate-600">Total Views</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-green-100 text-green-600 flex items-center justify-center">
                    <ShoppingCart className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{analytics?.overview?.total_sales || 0}</p>
                    <p className="text-sm text-slate-600">Total Sales</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-amber-100 text-amber-600 flex items-center justify-center">
                    <DollarSign className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">${(analytics?.overview?.total_revenue || 0).toFixed(2)}</p>
                    <p className="text-sm text-slate-600">Total Revenue</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-purple-100 text-purple-600 flex items-center justify-center">
                    <TrendingUp className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{(analytics?.overview?.conversion_rate || 0).toFixed(1)}%</p>
                    <p className="text-sm text-slate-600">Conversion Rate</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Charts Row */}
          <div className="grid lg:grid-cols-2 gap-6 mb-8">
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="font-heading text-lg">Views (Last 14 Days)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={getViewsChartData()}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="date" stroke="#64748b" fontSize={12} />
                      <YAxis stroke="#64748b" fontSize={12} />
                      <Tooltip />
                      <Line type="monotone" dataKey="views" stroke="#6366f1" strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="font-heading text-lg">Revenue (Last 14 Days)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={getSalesChartData()}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="date" stroke="#64748b" fontSize={12} />
                      <YAxis stroke="#64748b" fontSize={12} />
                      <Tooltip />
                      <Bar dataKey="revenue" fill="#22c55e" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Course Performance Table */}
          <Card className="border-slate-200 mb-8">
            <CardHeader>
              <CardTitle className="font-heading">Course Performance</CardTitle>
            </CardHeader>
            <CardContent>
              {analytics?.course_analytics?.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-3 px-4 font-medium text-slate-600">Course</th>
                        <th className="text-right py-3 px-4 font-medium text-slate-600">Price</th>
                        <th className="text-right py-3 px-4 font-medium text-slate-600">Views</th>
                        <th className="text-right py-3 px-4 font-medium text-slate-600">Sales</th>
                        <th className="text-right py-3 px-4 font-medium text-slate-600">Revenue</th>
                        <th className="text-right py-3 px-4 font-medium text-slate-600">Conv. Rate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analytics.course_analytics.map((course) => (
                        <tr key={course.course_id} className="border-b border-slate-100">
                          <td className="py-3 px-4 font-medium text-slate-900">{course.title}</td>
                          <td className="py-3 px-4 text-right text-slate-600">${course.price}</td>
                          <td className="py-3 px-4 text-right text-slate-600">{course.views}</td>
                          <td className="py-3 px-4 text-right text-slate-600">{course.sales}</td>
                          <td className="py-3 px-4 text-right text-green-600 font-medium">${course.revenue}</td>
                          <td className="py-3 px-4 text-right text-slate-600">{course.conversion_rate}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-center text-slate-500 py-8">No course data yet</p>
              )}
            </CardContent>
          </Card>

          {/* Payout & Coupons Row */}
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Payouts Section */}
            <Card className="border-slate-200">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="font-heading flex items-center gap-2">
                  <Wallet className="w-5 h-5" />
                  Payouts
                </CardTitle>
                <Dialog open={payoutDialog} onOpenChange={setPayoutDialog}>
                  <DialogTrigger asChild>
                    <Button size="sm" disabled={pendingPayout < 10} data-testid="request-payout-btn">
                      <DollarSign className="w-4 h-4 mr-1" />
                      Request Payout
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Request Payout</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="p-4 bg-green-50 rounded-lg text-center">
                        <p className="text-sm text-slate-600">Available Balance</p>
                        <p className="text-3xl font-bold text-green-600">${pendingPayout.toFixed(2)}</p>
                      </div>
                      <div className="space-y-2">
                        <Label>Amount to Withdraw</Label>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="Min. $10"
                          value={payoutAmount}
                          onChange={(e) => setPayoutAmount(e.target.value)}
                          data-testid="payout-amount-input"
                        />
                      </div>
                      <Button onClick={requestPayout} className="w-full" data-testid="confirm-payout-btn">
                        Request Payout
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                <div className="p-4 bg-slate-50 rounded-lg mb-4">
                  <p className="text-sm text-slate-600">Pending Balance</p>
                  <p className="text-2xl font-bold text-slate-900">${pendingPayout.toFixed(2)}</p>
                </div>
                {payouts.length > 0 ? (
                  <div className="space-y-2">
                    {payouts.slice(0, 5).map((payout) => (
                      <div key={payout.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div>
                          <p className="font-medium text-slate-900">${payout.amount.toFixed(2)}</p>
                          <p className="text-xs text-slate-500">{new Date(payout.requested_at).toLocaleDateString()}</p>
                        </div>
                        <Badge variant={payout.status === 'completed' ? 'default' : 'secondary'}>
                          {payout.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-slate-500 py-4">No payout history</p>
                )}
              </CardContent>
            </Card>

            {/* Coupons Section */}
            <Card className="border-slate-200">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="font-heading flex items-center gap-2">
                  <Tag className="w-5 h-5" />
                  Coupon Codes
                </CardTitle>
                <Dialog open={couponDialog} onOpenChange={setCouponDialog}>
                  <DialogTrigger asChild>
                    <Button size="sm" data-testid="create-coupon-btn">
                      <Plus className="w-4 h-4 mr-1" />
                      Create Coupon
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Create Coupon Code</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label>Coupon Code</Label>
                        <Input
                          placeholder="e.g., SUMMER20"
                          value={newCoupon.code}
                          onChange={(e) => setNewCoupon({ ...newCoupon, code: e.target.value.toUpperCase() })}
                          className="uppercase"
                          data-testid="coupon-code-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Discount Percentage</Label>
                        <Input
                          type="number"
                          min="1"
                          max="100"
                          value={newCoupon.discount_percent}
                          onChange={(e) => setNewCoupon({ ...newCoupon, discount_percent: parseInt(e.target.value) || 0 })}
                          data-testid="coupon-discount-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Max Uses</Label>
                        <Input
                          type="number"
                          min="1"
                          value={newCoupon.max_uses}
                          onChange={(e) => setNewCoupon({ ...newCoupon, max_uses: parseInt(e.target.value) || 100 })}
                          data-testid="coupon-max-uses-input"
                        />
                      </div>
                      <Button onClick={createCoupon} className="w-full" data-testid="save-coupon-btn">
                        Create Coupon
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                {coupons.length > 0 ? (
                  <div className="space-y-2">
                    {coupons.map((coupon) => (
                      <div key={coupon.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div>
                          <code className="font-mono font-bold text-primary">{coupon.code}</code>
                          <p className="text-sm text-slate-600">
                            {coupon.discount_percent}% off • {coupon.uses}/{coupon.max_uses} used
                          </p>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => deleteCoupon(coupon.id)}
                          className="text-slate-400 hover:text-destructive"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-slate-500 py-4">No coupons created yet</p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InstructorAnalytics;
